import { Outlet, useRouteLoaderData } from 'react-router-dom';
import { MenuItems, Sidebar } from './components/sidebar';
import { LoginResponseDto } from './lib/api';
import { checkPermission } from './utils/check-permissions';
import { Toaster } from '@/components/ui/toaster';
import SessionWarningModal from '@/shared/auth/session-warning-modal';
import { FeatureFlagProvider, useFeatureFlag } from './providers/feature-flag-provider';
import { useRouteTracking } from './hooks/useRouteTracking';

function getMenuItems(session: LoginResponseDto, isEnabled: ReturnType<typeof useFeatureFlag>['isEnabled']): MenuItems {
    if (session.activeOrg?.id === 'admin') {
        return {
            users: {
                url: '/admin/users',
                label: 'Users',
            },
            organization: {
                url: '/admin/organizations',
                label: 'Organizations',
            },
            featureFlags: {
                url: '/admin/feature-flags',
                label: 'Feature Flags',
            },
            auditLogs: {
                url: '/admin/audit-logs',
                label: 'Audit Logs',
            },
        };
    }
    return {
        dashboard:
            (checkPermission(session, 'admin-dashboard') || checkPermission(session, 'qa-dashboard')) && isEnabled('dashboard')
                ? {
                      url: '/dashboard',
                      label: 'Dashboard',
                  }
                : undefined,
        patients: checkPermission(session, 'patient')
            ? {
                  url: '/patients',
                  label: 'Patients',
              }
            : undefined,
        schedule: checkPermission(session, 'visit')
            ? {
                  url: '/schedule',
                  label: 'Schedule',
              }
            : undefined,
        qa: checkPermission(session, 'qa')
            ? {
                  url: '/qa',
                  label: 'QA',
              }
            : undefined,
        orders: checkPermission(session, 'orders')
            ? {
                  url: '/orders',
                  label: 'Orders',
              }
            : undefined,
        cms: checkPermission(session, 'cms')
            ? {
                  url: '/cms',
                  label: 'CMS',
              }
            : undefined,
        billing: checkPermission(session, 'billing')
            ? {
                  url: '/billing',
                  label: 'Billing',
              }
            : undefined,
        payroll: checkPermission(session, 'payroll')
            ? {
                  url: '/payroll',
                  label: 'Payroll',
              }
            : undefined,
        report:
            isEnabled('report-feature-flag') && checkPermission(session, 'report')
                ? {
                      url: '/report',
                      label: 'Reports',
                  }
                : undefined,
        organization: checkPermission(session)
            ? {
                  url: '/organization/overview',
                  label: 'Organization',
              }
            : undefined,
        settings: checkPermission(session)
            ? {
                  url: '/settings',
                  label: 'Settings',
              }
            : undefined,
    };
}

function AppContent() {
    const session = useRouteLoaderData('root') as LoginResponseDto;
    const { isEnabled } = useFeatureFlag();

    useRouteTracking(!!session?.activeOrg && session.activeOrg.id !== 'admin');

    return (
        <Sidebar menuItems={getMenuItems(session, isEnabled)}>
            <Outlet />
            <Toaster />
            <SessionWarningModal />
        </Sidebar>
    );
}

export function App() {
    return (
        <FeatureFlagProvider>
            <AppContent />
        </FeatureFlagProvider>
    );
}
